import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  Shield, 
  Clock, 
  Users, 
  Award, 
  Star, 
  CheckCircle, 
  BookOpen, 
  Globe,
  Mail,
  ArrowRight,
  Target,
  Zap,
  Lock,
  Home,
  ArrowLeft
} from "lucide-react";

interface EmbedCSCEProps {
  compact?: boolean;
  showHero?: boolean;
  showQuickFacts?: boolean;
  showLearningAreas?: boolean;
  showBenefits?: boolean;
  showIdealFor?: boolean;
  showNextSteps?: boolean;
  maxWidth?: string;
}

const EmbedCSCE = ({ 
  compact = false,
  showHero = true,
  showQuickFacts = true,
  showLearningAreas = true,
  showBenefits = true,
  showIdealFor = true,
  showNextSteps = true,
  maxWidth = "6xl"
}: EmbedCSCEProps) => {
  const keyAreas = [
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Physical Security Technologies",
      description: "Integration of physical security systems with cyber infrastructure"
    },
    {
      icon: <Lock className="w-6 h-6" />,
      title: "Threat Modeling & Compliance",
      description: "Advanced threat assessment and regulatory compliance frameworks"
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: "Protocol Integration",
      description: "OSDP, ONVIF, BACnet, and TLS protocol implementation"
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: "Access Control Models",
      description: "RBAC, ABAC, and PBAC implementation and management"
    },
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: "Framework Alignment",
      description: "GDPR, ISO 27001, and NIS2 compliance strategies"
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Cyber-Physical Integration",
      description: "Seamless integration of digital and physical security domains"
    }
  ];

  const benefits = [
    "Essential cybersecurity knowledge for converged environments",
    "Practical skills for integrated physical and digital security",
    "Foundation for career transition into converged security roles",
    "Collaboration readiness with IT teams and hybrid systems",
    "Industry-recognized credential from leading experts",
    "Digital badge for professional networking and verification"
  ];

  const idealFor = [
    "Physical security professionals transitioning to converged roles",
    "Facilities and operations staff entering cybersecurity",
    "IT professionals expanding into physical security integration",
    "Security managers overseeing hybrid environments",
    "Compliance officers working with converged systems",
    "Anyone starting their journey in converged security"
  ];

  return (
    <div className="bg-gradient-to-br from-pink-50 to-purple-50 font-sans relative">
      {/* Navigation Bar - Upper Right */}
      <div className="absolute top-4 right-6 flex gap-2 z-10">
        <Button 
          variant="outline" 
          size="sm"
          className="bg-white border-gray-300 hover:bg-gray-50"
          onClick={() => window.history.back()}
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </Button>
        <Button 
          variant="outline" 
          size="sm"
          className="bg-white border-gray-300 hover:bg-gray-50"
          onClick={() => window.open('https://convergedsecurity.es/', '_blank')}
        >
          <Home className="w-4 h-4 mr-1" />
          Home
        </Button>
      </div>
      {/* Hero Section */}
      {showHero && (
        <div className="bg-gradient-to-r from-pink-600 to-purple-700 text-white py-12">
          <div className={`max-w-${maxWidth} mx-auto px-4`}>
            <div className={`grid ${compact ? 'lg:grid-cols-1 text-center' : 'lg:grid-cols-2'} gap-8 items-center`}>
              <div>
                <Badge className="bg-white/20 text-white border-white/30 mb-3">
                  Foundational Certification
                </Badge>
                <h1 className={`${compact ? 'text-3xl md:text-4xl' : 'text-4xl md:text-5xl'} font-bold mb-4`}>
                  CSCE
                </h1>
                <h2 className={`${compact ? 'text-xl md:text-2xl' : 'text-2xl md:text-3xl'} font-semibold mb-3 text-pink-100`}>
                  Converged Security & Cybersecurity Expert
                </h2>
                <p className={`${compact ? 'text-base' : 'text-lg'} text-pink-100 mb-6 leading-relaxed`}>
                  Essential cybersecurity knowledge and practical skills for integrated physical and digital security environments.
                </p>
                {!compact && (
                  <div className="flex flex-wrap gap-3">
                    <Button 
                      size={compact ? "default" : "lg"} 
                      className="bg-white text-pink-600 hover:bg-pink-50"
                      onClick={() => window.open('mailto:info@convergedsecurity.es?subject=CSCE Certification Application&body=Hello, I would like to apply for the CSCE (Converged Security & Cybersecurity Expert) certification program. Please send me the application details and next steps.', '_blank')}
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Apply Now
                    </Button>
                    <Button 
                      size={compact ? "default" : "lg"} 
                      variant="outline" 
                      className="border-white text-white hover:bg-white/10"
                      onClick={() => window.open('https://pay.sumup.com/b2c/QI2N27DA', '_blank')}
                    >
                      <Star className="w-4 h-4 mr-2" />
                      Pay Now - €250
                    </Button>
                  </div>
                )}
              </div>
              {!compact && (
                <div className="flex justify-center">
                  <div className="relative">
                    <img 
                      src="./images/csce.png" 
                      alt="CSCE Certification Badge"
                      className="w-60 h-60 object-contain drop-shadow-2xl"
                    />
                    <div className="absolute -inset-4 bg-gradient-to-r from-pink-400/20 to-purple-400/20 rounded-full blur-xl"></div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Quick Facts */}
      {showQuickFacts && (
        <div className={`${compact ? 'py-8' : 'py-12'} bg-white`}>
          <div className={`max-w-${maxWidth} mx-auto px-4`}>
            <div className={`grid ${compact ? 'grid-cols-2 md:grid-cols-4' : 'md:grid-cols-4'} gap-4`}>
              <Card className="text-center border-pink-200">
                <CardContent className={compact ? "pt-4" : "pt-6"}>
                  <Star className={`${compact ? 'w-6 h-6' : 'w-8 h-8'} text-pink-600 mx-auto mb-2`} />
                  <h3 className={`font-bold ${compact ? 'text-lg' : 'text-2xl'} text-gray-900`}>€250</h3>
                  <p className={`${compact ? 'text-xs' : 'text-sm'} text-gray-600`}>Certification Fee</p>
                </CardContent>
              </Card>
              <Card className="text-center border-pink-200">
                <CardContent className={compact ? "pt-4" : "pt-6"}>
                  <Clock className={`${compact ? 'w-6 h-6' : 'w-8 h-8'} text-pink-600 mx-auto mb-2`} />
                  <h3 className={`font-bold ${compact ? 'text-lg' : 'text-2xl'} text-gray-900`}>3 Years</h3>
                  <p className={`${compact ? 'text-xs' : 'text-sm'} text-gray-600`}>Validity Period</p>
                </CardContent>
              </Card>
              <Card className="text-center border-pink-200">
                <CardContent className={compact ? "pt-4" : "pt-6"}>
                  <Users className={`${compact ? 'w-6 h-6' : 'w-8 h-8'} text-pink-600 mx-auto mb-2`} />
                  <h3 className={`font-bold ${compact ? 'text-sm' : 'text-xl'} text-gray-900`}>No Prerequisites</h3>
                  <p className={`${compact ? 'text-xs' : 'text-sm'} text-gray-600`}>Entry Level</p>
                </CardContent>
              </Card>
              <Card className="text-center border-pink-200">
                <CardContent className={compact ? "pt-4" : "pt-6"}>
                  <Award className={`${compact ? 'w-6 h-6' : 'w-8 h-8'} text-pink-600 mx-auto mb-2`} />
                  <h3 className={`font-bold ${compact ? 'text-lg' : 'text-2xl'} text-gray-900`}>24 Hours</h3>
                  <p className={`${compact ? 'text-xs' : 'text-sm'} text-gray-600`}>Badge Delivery</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      )}

      {/* Key Learning Areas */}
      {showLearningAreas && (
        <div className={compact ? 'py-8' : 'py-12'}>
          <div className={`max-w-${maxWidth} mx-auto px-4`}>
            <div className="text-center mb-8">
              <h2 className={`${compact ? 'text-2xl md:text-3xl' : 'text-3xl md:text-4xl'} font-bold text-gray-900 mb-3`}>
                What You'll Master
              </h2>
              <p className={`${compact ? 'text-base' : 'text-lg'} text-gray-600 max-w-2xl mx-auto`}>
                Comprehensive curriculum covering essential areas of converged security
              </p>
            </div>
            
            <div className={`grid ${compact ? 'md:grid-cols-2' : 'md:grid-cols-2 lg:grid-cols-3'} gap-6`}>
              {keyAreas.slice(0, compact ? 4 : 6).map((area, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow duration-300 border-pink-100">
                  <CardHeader className={compact ? "pb-2" : "pb-3"}>
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`p-2 bg-pink-100 rounded-lg text-pink-600`}>
                        {React.cloneElement(area.icon, { 
                          className: compact ? "w-4 h-4" : "w-6 h-6" 
                        })}
                      </div>
                      <CardTitle className={compact ? "text-base" : "text-lg"}>{area.title}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className={compact ? "pt-0" : ""}>
                    <p className={`${compact ? 'text-sm' : 'text-base'} text-gray-600`}>{area.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Program Benefits */}
      {showBenefits && (
        <div className={`${compact ? 'py-8' : 'py-12'} bg-gradient-to-r from-pink-600 to-purple-700 text-white`}>
          <div className={`max-w-${maxWidth} mx-auto px-4`}>
            <div className={`grid ${compact ? 'lg:grid-cols-1' : 'lg:grid-cols-2'} gap-8 items-center`}>
              <div>
                <h2 className={`${compact ? 'text-2xl md:text-3xl' : 'text-3xl md:text-4xl'} font-bold mb-4`}>
                  Why Choose CSCE?
                </h2>
                <p className={`${compact ? 'text-base' : 'text-lg'} text-pink-100 mb-6`}>
                  The CSCE certification provides foundational knowledge for converged security.
                </p>
                <div className="space-y-3">
                  {benefits.slice(0, compact ? 4 : 6).map((benefit, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} text-pink-200 flex-shrink-0 mt-0.5`} />
                      <p className={`${compact ? 'text-sm' : 'text-base'} text-pink-100`}>{benefit}</p>
                    </div>
                  ))}
                </div>
              </div>
              {!compact && (
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                  <h3 className="text-xl font-bold mb-4">Program Highlights</h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <BookOpen className="w-4 h-4 text-pink-200" />
                      <span className="text-sm">Comprehensive assessment and exam</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Users className="w-4 h-4 text-pink-200" />
                      <span className="text-sm">Led by Dr. Vladimir Bunic, PhD</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Globe className="w-4 h-4 text-pink-200" />
                      <span className="text-sm">Digital certificate and badge</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Shield className="w-4 h-4 text-pink-200" />
                      <span className="text-sm">EU compliant certification</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Ideal Candidates */}
      {showIdealFor && (
        <div className={`${compact ? 'py-8' : 'py-12'} bg-white`}>
          <div className={`max-w-${maxWidth} mx-auto px-4`}>
            <div className="text-center mb-8">
              <h2 className={`${compact ? 'text-2xl md:text-3xl' : 'text-3xl md:text-4xl'} font-bold text-gray-900 mb-3`}>
                Perfect For
              </h2>
              <p className={`${compact ? 'text-base' : 'text-lg'} text-gray-600 max-w-2xl mx-auto`}>
                CSCE is designed for professionals entering converged security
              </p>
            </div>
            
            <div className={`grid ${compact ? 'md:grid-cols-2' : 'md:grid-cols-2 lg:grid-cols-3'} gap-4`}>
              {idealFor.slice(0, compact ? 4 : 6).map((candidate, index) => (
                <Card key={index} className="border-pink-100 hover:border-pink-300 transition-colors">
                  <CardContent className={compact ? "pt-4" : "pt-6"}>
                    <div className="flex items-start gap-3">
                      <ArrowRight className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} text-pink-600 flex-shrink-0 mt-0.5`} />
                      <p className={`${compact ? 'text-sm' : 'text-base'} text-gray-700`}>{candidate}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Next Steps */}
      {showNextSteps && (
        <div className={`${compact ? 'py-8' : 'py-12'} bg-gradient-to-br from-pink-50 to-purple-50`}>
          <div className={`max-w-${compact ? '3xl' : '4xl'} mx-auto px-4 text-center`}>
            <h2 className={`${compact ? 'text-2xl md:text-3xl' : 'text-3xl md:text-4xl'} font-bold text-gray-900 mb-4`}>
              Ready to Start?
            </h2>
            <p className={`${compact ? 'text-base' : 'text-lg'} text-gray-600 mb-6`}>
              Join professionals worldwide who have chosen CSCE as their foundation
            </p>
            
            <Card className="border-pink-200 shadow-lg">
              <CardContent className={compact ? "pt-6" : "pt-8"}>
                <div className={`grid ${compact ? 'grid-cols-3' : 'md:grid-cols-3'} gap-4 mb-6`}>
                  <div className="text-center">
                    <div className={`${compact ? 'w-8 h-8' : 'w-12 h-12'} bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-2`}>
                      <span className={`text-pink-600 font-bold ${compact ? 'text-sm' : ''}`}>1</span>
                    </div>
                    <h3 className={`font-semibold mb-1 ${compact ? 'text-sm' : ''}`}>Apply</h3>
                    <p className={`${compact ? 'text-xs' : 'text-sm'} text-gray-600`}>Submit application and pay fee</p>
                  </div>
                  <div className="text-center">
                    <div className={`${compact ? 'w-8 h-8' : 'w-12 h-12'} bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-2`}>
                      <span className={`text-pink-600 font-bold ${compact ? 'text-sm' : ''}`}>2</span>
                    </div>
                    <h3 className={`font-semibold mb-1 ${compact ? 'text-sm' : ''}`}>Learn</h3>
                    <p className={`${compact ? 'text-xs' : 'text-sm'} text-gray-600`}>Complete program and assessment</p>
                  </div>
                  <div className="text-center">
                    <div className={`${compact ? 'w-8 h-8' : 'w-12 h-12'} bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-2`}>
                      <span className={`text-pink-600 font-bold ${compact ? 'text-sm' : ''}`}>3</span>
                    </div>
                    <h3 className={`font-semibold mb-1 ${compact ? 'text-sm' : ''}`}>Certified</h3>
                    <p className={`${compact ? 'text-xs' : 'text-sm'} text-gray-600`}>Receive badge within 24 hours</p>
                  </div>
                </div>
                
                <div className="flex flex-wrap justify-center gap-3">
                  <Button 
                    size={compact ? "default" : "lg"} 
                    className="bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700"
                    onClick={() => window.open('https://pay.sumup.com/b2c/QI2N27DA', '_blank')}
                  >
                    <Star className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} mr-2`} />
                    Pay Now - €250
                  </Button>
                  <Button 
                    size={compact ? "default" : "lg"} 
                    variant="outline" 
                    className="border-pink-300 text-pink-600 hover:bg-pink-50"
                    onClick={() => window.open('mailto:info@convergedsecurity.es?subject=CSCE Certification Application&body=Hello, I would like to apply for the CSCE (Converged Security & Cybersecurity Expert) certification program. Please send me the application details and next steps.', '_blank')}
                  >
                    <Mail className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} mr-2`} />
                    Contact Us
                  </Button>
                  {!compact && (
                    <Button 
                      size="lg" 
                      variant="outline" 
                      className="border-pink-300 text-pink-600 hover:bg-pink-50"
                      onClick={() => window.open('mailto:info@convergedsecurity.es?subject=CSCE Brochure Request&body=Hello, I would like to download the CSCE (Converged Security & Cybersecurity Expert) certification brochure. Please send me the detailed program information.', '_blank')}
                    >
                      <BookOpen className="w-5 h-5 mr-2" />
                      Download Brochure
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmbedCSCE;